import { Minus, Plus } from "lucide-react";
import * as React from "react";
import { useMemo, useState } from "react";

import { RenderIf } from "../../common/RenderIf";
import { cn } from "../../lib/utils";

interface TreeNodeProps {
	children?: React.ReactNode;
	className?: string;
	isOpen?: boolean;
	title: React.ReactNode;
	initialIsExpanded?: boolean;
	isExpandable?: boolean;
	onExpandedChange?: (isExpanded: boolean) => void;
	onClick?: () => void;
	expansionIcons?: {
		collapse: React.ReactNode;
		expand: React.ReactNode;
	};
}

const TreeNode = React.forwardRef<HTMLDivElement, TreeNodeProps>(
	(
		{
			children = null,
			className,
			title,
			initialIsExpanded = false,
			isExpandable = true,
			expansionIcons = {
				collapse: <Minus size={16} />,
				expand: <Plus size={16} />,
			},
			onClick,
			isOpen: controlledIsOpen,
			onExpandedChange,
		},
		ref,
	) => {
		const [internalIsOpen, setInternalIsOpen] = useState(initialIsExpanded ?? false);
		const isControlled = controlledIsOpen !== undefined;
		const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

		const treeChildren = useMemo(() => {
			if (!children) return null;
			return React.Children.map(children, (child) => {
				if (!React.isValidElement(child)) return child;
				const childProps = child.props as { className?: string };
				return React.cloneElement(child, {
					className: cn("border-l-2 border-gray-200 pl-2", childProps.className),
				} as React.HTMLAttributes<HTMLElement>);
			});
		}, [children]);

		const onExpansionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();

			if (isControlled) {
				onExpandedChange?.(!isOpen);
			} else {
				setInternalIsOpen((_isOpen) => !_isOpen);
			}
		};

		return (
			<div className="flex flex-col">
				<div
					role="button"
					onClick={onClick}
					className={cn(
						"flex justify-between w-full items-center",
						{
							"mb-1": Boolean(treeChildren) && isOpen,
						},
						className,
					)}
				>
					{title}
					<RenderIf condition={isExpandable && Boolean(treeChildren)}>
						<button
							className="px-2 hover:text-black rounded-md"
							onClick={onExpansionClick}
						>
							<RenderIf condition={isOpen}>{expansionIcons.collapse}</RenderIf>
							<RenderIf condition={!isOpen}>{expansionIcons.expand}</RenderIf>
						</button>
					</RenderIf>
				</div>
				<RenderIf condition={isOpen}>{treeChildren}</RenderIf>
			</div>
		);
	},
);

TreeNode.displayName = "TreeNode";

export { TreeNode };
